import simplify from 'simplify-js';

export const emptyAreas = () => ({ type: 'FeatureCollection', features: [] });
export const validPosition = (p) =>
  Array.isArray(p) &&
  p.length >= 2 &&
  Number.isFinite(p[0]) &&
  Number.isFinite(p[1]) &&
  Math.abs(p[0]) <= 180 &&
  Math.abs(p[1]) <= 90;

export function geometryPositions(geometry) {
  if (!geometry) return [];
  if (geometry.type === 'GeometryCollection')
    return Array.isArray(geometry.geometries)
      ? geometry.geometries.flatMap(geometryPositions)
      : [];
  const depths = {
    Point: 0,
    MultiPoint: 1,
    LineString: 1,
    MultiLineString: 2,
    Polygon: 2,
    MultiPolygon: 3,
  };
  const depth = depths[geometry.type];
  if (depth === undefined) return [];
  const visit = (coordinates, level) => {
    if (!level) return validPosition(coordinates) ? [coordinates] : [];
    return Array.isArray(coordinates)
      ? coordinates.flatMap((child) => visit(child, level - 1))
      : [];
  };
  return visit(geometry.coordinates, depth);
}

function validGeometry(geometry) {
  if (!geometry) return false;
  const line = (points, minimum) =>
    Array.isArray(points) &&
    points.length >= minimum &&
    points.every(validPosition);
  const ring = (points) =>
    line(points, 4) &&
    points[0][0] === points[points.length - 1][0] &&
    points[0][1] === points[points.length - 1][1];
  const many = (items, validator) =>
    Array.isArray(items) && items.length > 0 && items.every(validator);
  const shapes = {
    Point: validPosition,
    MultiPoint: (points) => line(points, 1),
    LineString: (points) => line(points, 2),
    MultiLineString: (lines) => many(lines, (points) => line(points, 2)),
    Polygon: (rings) => many(rings, ring),
    MultiPolygon: (polygons) => many(polygons, (rings) => many(rings, ring)),
  };
  if (geometry.type === 'GeometryCollection')
    return many(geometry.geometries, validGeometry);
  return Boolean(
    shapes[geometry.type] && shapes[geometry.type](geometry.coordinates)
  );
}

export function normalizeAreas(value) {
  let data = value;
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch (_) {
      return emptyAreas();
    }
  }
  if (!data || typeof data !== 'object') return emptyAreas();
  let features;
  if (data.type === 'FeatureCollection') features = data.features;
  else if (data.type === 'Feature') features = [data];
  else features = [{ type: 'Feature', properties: {}, geometry: data }];
  if (!Array.isArray(features)) return emptyAreas();
  return {
    type: 'FeatureCollection',
    features: features.filter(
      (feature) =>
        feature && feature.type === 'Feature' && validGeometry(feature.geometry)
    ),
  };
}

export function polygon(points) {
  const vertices = [];
  points.forEach((point) => {
    const last = vertices[vertices.length - 1];
    if (
      validPosition(point) &&
      (!last || point[0] !== last[0] || point[1] !== last[1])
    )
      vertices.push(point.slice(0, 2));
  });
  if (
    vertices.length > 1 &&
    vertices[0][0] === vertices[vertices.length - 1][0] &&
    vertices[0][1] === vertices[vertices.length - 1][1]
  )
    vertices.pop();
  if (vertices.length < 3) return null;
  const [origin] = vertices;
  const area = vertices.reduce((sum, point, i) => {
    const next = vertices[(i + 1) % vertices.length];
    return (
      sum +
      (point[0] - origin[0]) * (next[1] - origin[1]) -
      (next[0] - origin[0]) * (point[1] - origin[1])
    );
  }, 0);
  if (Math.abs(area) < 1e-14) return null;
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [[...vertices, vertices[0].slice()]],
    },
  };
}

export function rectangle(start, end) {
  if (!validPosition(start) || !validPosition(end)) return null;
  return polygon([start, [end[0], start[1]], end, [start[0], end[1]]]);
}

export function circle(center, edge) {
  if (!validPosition(center) || !validPosition(edge)) return null;
  const rad = (degrees) => (degrees * Math.PI) / 180;
  const latitude = rad(center[1]);
  const longitude = rad(center[0]);
  const haversine =
    Math.sin(rad(edge[1] - center[1]) / 2) ** 2 +
    Math.cos(latitude) *
      Math.cos(rad(edge[1])) *
      Math.sin(rad(edge[0] - center[0]) / 2) ** 2;
  const radius = 2 * Math.asin(Math.sqrt(Math.min(1, haversine)));
  return polygon(
    Array.from({ length: 64 }, (_, i) => {
      const bearing = (2 * Math.PI * i) / 64;
      const lat = Math.asin(
        Math.sin(latitude) * Math.cos(radius) +
          Math.cos(latitude) * Math.sin(radius) * Math.cos(bearing)
      );
      const lng =
        longitude +
        Math.atan2(
          Math.sin(bearing) * Math.sin(radius) * Math.cos(latitude),
          Math.cos(radius) - Math.sin(latitude) * Math.sin(lat)
        );
      return [
        (((lng * 180) / Math.PI + 540) % 360) - 180,
        (lat * 180) / Math.PI,
      ];
    })
  );
}

export const freehand = (samples) =>
  polygon(simplify(samples, 2, true).map((sample) => sample.coordinate));
export function appendArea(existing, feature) {
  const data = normalizeAreas(existing);
  return { ...data, features: [...data.features, feature] };
}
